import { useState, useRef, useCallback } from "react";

interface PaginationSetters {
  setItems: (items: any[]) => void;
  setTotalDocs: (total: number) => void;
  setTotalPages: (total: number) => void;
  setCurrentPage: (page: number) => void;
  setIsLoading: (loading: boolean) => void;
}

/**
 * useSearchBar Hook
 * Manages search state and syncs results with firestore pagination.
 *
 * This hook encapsulates the boilerplate code for handling search results
 * from the SearchBar component and updating the pagination state.
 *
 * @param pagination - The setters returned by useFirestorePagination
 * @param itemsPerPage - Number of items shown per page (default 20)
 * @returns State and handlers for the SearchBar component
 */
export function useSearchBar(
  pagination: PaginationSetters,
  itemsPerPage: number = 20,
  externalSearchMode?: boolean,
  externalSetIsSearchMode?: (val: boolean) => void,
) {
  const [internalSearchMode, setInternalSearchMode] = useState(false);
  const isSearchMode =
    externalSearchMode !== undefined ? externalSearchMode : internalSearchMode;
  const setIsSearchMode =
    externalSetIsSearchMode !== undefined
      ? externalSetIsSearchMode
      : setInternalSearchMode;

  // Use a ref for pagination to prevent re-creation of handleSearchResults
  // when the parent passes a new pagination object (which happens on every render).
  const paginationRef = useRef(pagination);

  // Always keep ref in sync
  if (paginationRef.current !== pagination) {
    paginationRef.current = pagination;
  }

  const searchBarRef = useRef<any>(null);

  /**
   * Callback to handle search results from the SearchBar component.
   * Syncs the search results with the pagination state.
   */
  const handleSearchResults = useCallback(
    ({ results, total, isSearching, page }: any) => {
      const p = paginationRef.current;
      if (isSearching) {
        setIsSearchMode(true);
        p.setItems(results);
        p.setTotalDocs(total);
        p.setTotalPages(Math.max(1, Math.ceil(total / itemsPerPage)));
        p.setCurrentPage(page || 1);
      } else {
        // When search is cleared, exit search mode.
        // useFirestorePagination will detect this change (when isSearchMode is passed as a prop)
        // and automatically refresh/restore the normal listing.
        setIsSearchMode(false);
      }
    },
    [itemsPerPage, setIsSearchMode], // pagination removed from dependency
  );

  /**
   * Manually clear the search bar and exit search mode.
   */
  const clearSearch = useCallback(() => {
    if (
      searchBarRef.current &&
      typeof searchBarRef.current.clear === "function"
    ) {
      searchBarRef.current.clear();
    }
    setIsSearchMode(false);
  }, []);

  return {
    isSearchMode,
    setIsSearchMode,
    searchBarRef,
    handleSearchResults,
    onLoading: pagination.setIsLoading,
    clearSearch,
  };
}
